<template>
  <v-stepper v-model="e1">
    <v-stepper-header>
      <v-stepper-step
        :complete="e1 > 1"
        step="1"
      >
        Enter Settlement Amount
      </v-stepper-step>

      <v-divider />

      <v-stepper-step
        :complete="e1 > 2"
        step="2"
      >
        Payment Method
      </v-stepper-step>
    </v-stepper-header>

    <v-stepper-items>
      <!--Enter settlement amount screen-->
      <v-stepper-content
        class="settlement-amount-step"
        step="1"
      >
        <v-container>
          <v-row>
            <v-col cols="auto">
              <v-alert
                color="info"
                theme="dark"
                icon="mdi-bullhorn-outline"
              >
                <div class="text-h6">
                  Agent, Please ask the customer the question below:
                </div>
                Please ask the borrower what they would like to settle for?
                If the amount does not fall
                within acceptable range, please offer the borrower the minimum acceptable.
                If the borrower cannot afford the minimum, please offer the Reduced Payoff
                Plan option.
              </v-alert>
            </v-col>
          </v-row>
          <v-row
            justify="center"
          >
            <v-col cols="auto">
              <p>Minimum Acceptable Amount</p>
              <p class="amount">
                {{ minSettlementAmount | currency }}
              </p>
            </v-col>
            <v-col cols="auto">
              <p>Maximum Acceptable Amount</p>
              <p class="amount">
                {{ maxSettlementAmount | currency }}
              </p>
            </v-col>
            <v-col cols="auto">
              <p>Enter Settlement Amount</p>
              <v-form ref="settlementAmountForm">
                <v-text-field
                  v-model="formData.settlementAmount"
                  small
                  :rules="[
                    rules.settlementAmount.required,
                    rules.settlementAmount.minAmount,
                    rules.settlementAmount.maxAmount]"
                  type="number"
                  placeholder="0.00"
                  outlined
                />
              </v-form>
            </v-col>
          </v-row>
          <v-row justify="space-between">
            <v-col
              cols="auto"
              align="left"
            >
              <v-btn
                outlined
                large
                color="primary"
                @click="goToReducedPayoffPaymentView"
              >
                <v-icon
                  left
                  dark
                >
                  mdi-arrow-left
                </v-icon>
                Reduced Payoff Plan Option
              </v-btn>
            </v-col>
            <v-col
              cols="auto"
              align="right"
            >
              <v-btn
                color="success"
                large
                :disabled="!settlementAmountFormActionDisabled"
                @click="e1 = 2"
              >
                Continue to Payment Method
                <v-icon
                  right
                  dark
                >
                  mdi-arrow-right
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-stepper-content>
      <!--Enter settlement amount screen End-->

      <!--Payment Method screen-->
      <v-stepper-content step="2">
        <v-form
          ref="singlePaymentForm"
          novalidate
          @submit.prevent="onSubmit"
        >
          <v-container>
            <v-row>
              <v-col
                cols="12"
                align="right"
              >
                <ticket-add-debit-card-dialog
                  :ticket="ticket"
                  @success="reloadPaymentProfiles"
                />
                <ticket-add-checking-dialog
                  :ticket="ticket"
                  @success="reloadPaymentProfiles"
                />
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="formData.paymentAccountId"
                  :items="paymentAccountsWithManualOption"
                  item-value="id"
                  item-text="title"
                  label="Payment Account"
                  outlined
                  required
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="formData.paymentDate"
                  type="date"
                  label="Payment Date"
                  :min="minPaymentDate"
                  :max="maxPaymentDate"
                  :rules="[
                    rules.paymentDate.required,
                    rules.paymentDate.minDate,
                    rules.paymentDate.maxDate
                  ]"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                v-if="displayCSRMessageForManualPayment"
                cols="12"
              >
                <v-alert
                  color="info"
                  theme="dark"
                  icon="mdi-bullhorn-outline"
                >
                  <div class="text-h6">
                    Agent, Please read to customer:
                  </div>
                  Please note that because you selected the manual payments option,
                  you will be responsible for completing your payment.
                </v-alert>
              </v-col>
              <v-col
                v-else-if="displayCSRMessageForAutopay"
                cols="12"
              >
                <v-alert
                  color="info"
                  theme="dark"
                  icon="mdi-bullhorn-outline"
                >
                  <div class="text-h6">
                    Agent, Please read to customer:
                  </div>
                  Please acknowledge that you have verbally consented to the ACH Authorization.
                  The Electronic disclosure will be included in your confirmation email.
                </v-alert>
                <v-checkbox
                  v-model="formData.achConsent"
                  label="Verbally Opt into Autopay"
                />
              </v-col>
            </v-row>
          </v-container>
          <v-row justify="space-between">
            <v-col
              cols="auto"
              align="left"
            >
              <v-btn
                outlined
                large
                color="primary"
                @click="e1 = 1"
              >
                <v-icon
                  left
                  dark
                >
                  mdi-arrow-left
                </v-icon>
                Back
              </v-btn>
            </v-col>
            <v-col
              cols="auto"
              align="right"
            >
              <v-btn
                color="success"
                large
                :disabled="createSettlePlanButtonDisabled"
                :loading="loading"
                @click="createSettlementPlan"
              >
                Create Settlement Plan
                <v-icon
                  right
                  dark
                >
                  mdi-arrow-right
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-stepper-content>
      <!--Payment Method screen End-->
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import { DateTime } from 'luxon';
import TicketAddDebitCardDialog from '@/views/Tickets/AddPaymentDialogs/TicketAddDebitCardDialog.vue';
import TicketAddCheckingDialog from '@/views/Tickets/AddPaymentDialogs/TicketAddCheckingDialog.vue';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'SinglePaymentPlan',

  components: {
    TicketAddCheckingDialog,
    TicketAddDebitCardDialog,
  },

  props: {
    ticket: {
      type: Object,
      required: true,
    },
    minSettlementAmount: {
      type: Number,
      required: true,
    },
    maxSettlementAmount: {
      type: Number,
      required: true,
    },
    paymentAccounts: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      e1: 1,
      formData: {
        settlementAmount: null,
        paymentDate: new Date().toISOString().slice(0, 10),
        paymentAccountId: null,
        achConsent: false,
        platform: 'HUI',
      },
      isValidSettlementAmount: true,
      loading: false,
    };
  },

  computed: {
    ...mapState('settings', ['settings']),

    paymentAccountsWithManualOption() {
      const manualItem = {
        active: 1,
        id: -1,
        is_primary: 0,
        is_secondary: 0,
        title: 'Manual Payments',
        type: 'paymentAccount.type.manual',
        visible: 1,
      };
      return [
        ...this.paymentAccounts,
        manualItem,
      ];
    },
    isManualPayment() {
      return this.formData.paymentAccountId === -1;
    },
    isDebitCard() {
      return this.paymentAccountsWithManualOption.some((payment) => payment.type === 'paymentAccount.type.credit' && payment.id === this.formData.paymentAccountId);
    },
    displayCSRMessageForManualPayment() {
      return (!this.formData.paymentAccountId) ? false
        : this.paymentAccountsWithManualOption.some((payment) => payment.type === 'paymentAccount.type.manual' && payment.id === this.formData.paymentAccountId);
    },
    displayCSRMessageForAutopay() {
      return (!this.formData.paymentAccountId) ? false
        : this.paymentAccountsWithManualOption.some((payment) => ['paymentAccount.type.checking', 'paymentAccount.type.credit'].includes(payment.type) && payment.id === this.formData.paymentAccountId);
    },
    settlementAmountFormActionDisabled() {
      return this.formData.settlementAmount
        && (this.formData.settlementAmount >= this.minSettlementAmount
          && this.formData.settlementAmount <= this.maxSettlementAmount);
    },
    createSettlePlanButtonDisabled() {
      return !this.formData.paymentAccountId
        || !this.formData.paymentDate
        || (!this.formData.achConsent && !this.isManualPayment);
    },
    isSameDayAchEnabled() {
      return this.settings?.brand?.enable_same_day_ach || false;
    },
    minPaymentDate() {
      // if payment method is a debit card or same day ACH is enabled, return today
      if (this.isDebitCard || this.isSameDayAchEnabled) {
        return DateTime.local().toISODate();
      }

      const now = DateTime.now().setZone('America/Chicago').toObject();
      const hours = now.hour;
      const daysToIncrement = hours >= 15 ? 2 : 1;

      // else return the next biz day
      return DateTime.local().plusBusiness({ days: daysToIncrement }).toISODate();
    },
    maxPaymentDate() {
      return DateTime.local().plus({ days: 31 }).toFormat('yyyy-LL-dd');
    },
    rules() {
      return {
        settlementAmount: {
          required: (value) => !!value || 'Required.',
          maxAmount: (value) => value <= this.maxSettlementAmount || `Amount cannot be greater than ${this.maxSettlementAmount}`,
          minAmount: (value) => value >= this.minSettlementAmount || `Amount cannot be less than ${this.minSettlementAmount}`,
        },
        paymentDate: {
          required: (value) => !!value || 'Required.',
          maxDate: (value) => value <= this.maxPaymentDate || `Payment date cannot be greater than ${this.maxPaymentDate}`,
          minDate: (value) => value >= this.minPaymentDate || `Payment date cannot be less than ${this.minPaymentDate}`,
        },
      };
    },
  },

  watch: {
    'formData.paymentAccountId': {
      handler(newVal) {
        if (!newVal) {
          this.formData.achConsent = false;
        }
        this.formData.paymentDate = this.minPaymentDate;
      },
    },
  },

  methods: {
    ...mapActions('settlement', ['createSettlementSinglePaymentPlan']),
    ...mapActions('notification', ['setNotification']),

    goToReducedPayoffPaymentView() {
      this.$emit('display-reduced-payoff-plan-option-view');
    },

    async reloadPaymentProfiles() {
      const res = await this.$axios
        .post(`api/v1/loanpro-customer/${this.ticket.lpCustomer.id}/payment-profiles`, {
          ticketId: this.$route.params.id,
        });

      this.paymentAccounts = [...res.data.data];
    },

    async createSettlementPlan() {
      this.loading = true;

      try {
        const result = await this.createSettlementSinglePaymentPlan({
          ticketId: this.ticket.zendesk.id,
          lpLoan: this.ticket.active_loan.id,
          formData: this.formData,
        });

        this.loading = false;

        if (result.status === 200) {
          const msg = 'Single Payment Settlement was completed';
          this.setNotification({
            message: msg,
            color: 'green',
          });
          this.$emit('display-settlement-final-actions');
        }
      } catch (e) {
        this.loading = false;
        const msg = 'There was an error processing your request';
        this.setNotification({
          message: msg,
          color: 'red',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .settlement-amount-step {
    .amount {
      font-size: 1.5rem;
      font-weight: 500;
      padding-top: 1rem;
    }
  }
</style>
